import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Sheet, SheetBody, SheetContent, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useConfirm } from '@/hooks/useConfirm';
import { useDeleteIntegration } from '@/api/integrations/use-integrations';
import type { TenantIntegration } from '@/api/integrations/integrationService';
import { getChannel, type CatalogChannel, type CatalogProvider } from './catalog';
import { DrawerHeading } from './DrawerHeading';
import { deriveChannelStatus } from './integrationHelpers';
import { ProviderFields } from './ProviderFields';
import { ProviderPicker } from './ProviderPicker';
import { useMetaLine } from './useMetaLine';
import { useProviderForm } from './useProviderForm';
import { VerifySection } from './VerifySection';

interface ServiceDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    channelId: 'voip' | 'email' | 'whatsapp' | 'sms';
    /** Every saved row of this channel. */
    rows: TenantIntegration[];
    canEdit: boolean;
    /** Credentials-only check (INTEGRATIONS view). */
    canCheck: boolean;
}

/** Catalog providers plus any saved row whose provider the catalog doesn't know (so it can be removed). */
function withUnknownProviders(
    channel: CatalogChannel,
    rows: TenantIntegration[]
): CatalogProvider[] {
    const known = new Set(channel.providers.map(provider => provider.id));
    const unknown = rows
        .filter(row => !known.has(row.provider_name))
        .map<CatalogProvider>(row => ({
            id: row.provider_name,
            name: row.provider_name,
            description: 'integrations.providers.unknown.description',
            fields: [],
        }));
    return [...channel.providers, ...unknown];
}

function initialProvider(providers: CatalogProvider[], rows: TenantIntegration[]): string {
    const newest = [...rows].sort(
        (a, b) => (Date.parse(b.updated_at) || 0) - (Date.parse(a.updated_at) || 0)
    );
    const active = newest.find(row => row.is_active);
    return (active ?? newest[0])?.provider_name ?? providers[0].id;
}

interface EditorProps {
    channel: CatalogChannel;
    provider: CatalogProvider;
    providers: CatalogProvider[];
    row?: TenantIntegration;
    /** Name of the provider of this channel that is on now, when it isn't the one being edited. */
    otherActiveName?: string;
    rowsByProvider: Record<string, TenantIntegration | undefined>;
    onProviderChange: (providerId: string) => void;
    onClose: () => void;
    canEdit: boolean;
    canCheck: boolean;
}

/** Everything under the header for one selected provider: strip, form, verify, footer. */
const ServiceEditor: React.FC<EditorProps> = ({
    channel,
    provider,
    providers,
    row,
    otherActiveName,
    rowsByProvider,
    onProviderChange,
    onClose,
    canEdit,
    canCheck,
}) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    const deleteMutation = useDeleteIntegration();
    const describe = useMetaLine();

    const { form, isSaving, generalError, save } = useProviderForm({
        type: channel.id,
        provider,
        row,
    });

    const savedEnabled = row ? row.is_active : true;
    const [enabled, setEnabled] = useState(savedEnabled);
    useEffect(() => setEnabled(savedEnabled), [savedEnabled, row?.id]);

    const status = deriveChannelStatus(row ? [row] : []);
    const meta = describe(status);
    const dirty = form.formState.isDirty;

    const remove = async () => {
        if (!row) return;
        const ok = await confirm({
            title: t('integrations.confirm.removeTitle', { name: provider.name }),
            description: t('integrations.confirm.removeDescription', {
                service: t(channel.name),
            }),
            confirmLabel: t('integrations.actions.remove'),
            variant: 'destructive',
        });
        if (!ok) return;
        try {
            await deleteMutation.mutateAsync(row.id);
            enqueueSnackbar(t('integrations.toasts.removed', { name: provider.name }), {
                variant: 'success',
            });
        } catch {
            enqueueSnackbar(t('integrations.toasts.removeFailed'), { variant: 'error' });
        }
    };

    return (
        <Form {...form}>
            <form
                noValidate
                onSubmit={event => {
                    event.preventDefault();
                    void save(canEdit ? enabled : undefined);
                }}
                className="flex min-h-0 flex-1 flex-col"
            >
                <SheetBody className="space-y-6">
                    {/* Status strip */}
                    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/40 p-3">
                        <div className="min-w-0 space-y-1">
                            <StatusBadge
                                kind="integration"
                                status={status.state}
                                label={t(`integrations.status.${status.state}`)}
                            />
                            <p className="text-xs text-muted-foreground">{meta.text}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <label
                                htmlFor="integration-enabled"
                                className="text-sm font-medium text-foreground"
                            >
                                {t('integrations.drawer.enabled')}
                            </label>
                            <Switch
                                id="integration-enabled"
                                checked={enabled}
                                onCheckedChange={setEnabled}
                                disabled={!canEdit}
                            />
                        </div>
                    </div>
                    {enabled && otherActiveName && canEdit && (
                        <p className="-mt-3 text-xs text-muted-foreground">
                            {t('integrations.drawer.turnsOff', { name: otherActiveName })}
                        </p>
                    )}

                    {/* Provider */}
                    {providers.length > 1 && (
                        <section className="space-y-3">
                            <h3 className="text-sm font-semibold text-foreground">
                                {t('integrations.sections.provider')}
                            </h3>
                            <ProviderPicker
                                providers={providers}
                                value={provider.id}
                                onChange={onProviderChange}
                                rows={rowsByProvider}
                                ariaLabel={t('integrations.sections.provider')}
                            />
                        </section>
                    )}

                    {generalError && (
                        <Alert variant="destructive" role="alert">
                            <AlertDescription>{generalError}</AlertDescription>
                        </Alert>
                    )}

                    <ProviderFields
                        form={form}
                        provider={provider}
                        saved={row?.configuration}
                        disabled={!canEdit}
                    />

                    <VerifySection
                        channel={channel.id}
                        providerName={provider.name}
                        row={row}
                        dirty={dirty}
                        canCheck={canCheck && provider.fields.length > 0}
                        checkSupported={provider.checkSupported !== false}
                        canSend={canEdit && provider.fields.length > 0}
                        isActive={!!row?.is_active}
                    />
                </SheetBody>

                <SheetFooter>
                    {canEdit && row && (
                        <Button
                            type="button"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={remove}
                            disabled={deleteMutation.isPending}
                        >
                            <Trash2 className="h-4 w-4" />
                            {t('integrations.actions.remove')}
                        </Button>
                    )}
                    <div className="ms-auto flex items-center gap-2">
                        <Button type="button" variant="outline" onClick={onClose}>
                            {canEdit ? t('actions.cancel') : t('integrations.actions.close')}
                        </Button>
                        {canEdit && provider.fields.length > 0 && (
                            <Button type="submit" disabled={isSaving}>
                                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                                {t('actions.save')}
                            </Button>
                        )}
                    </div>
                </SheetFooter>
            </form>
        </Form>
    );
};

/**
 * Drawer for voip, email, whatsapp and sms: status strip with the Enabled switch, provider
 * radio cards, the grouped credentials form, a Verify section and Remove / Cancel / Save.
 */
export const ServiceDrawer: React.FC<ServiceDrawerProps> = ({
    open,
    onOpenChange,
    channelId,
    rows,
    canEdit,
    canCheck,
}) => {
    const { t } = useTranslation('crm');
    const channel = getChannel(channelId);
    const providers = useMemo(() => withUnknownProviders(channel, rows), [channel, rows]);
    const [providerId, setProviderId] = useState(() => initialProvider(providers, rows));

    const rowsByProvider = useMemo(() => {
        const map: Record<string, TenantIntegration | undefined> = {};
        for (const row of rows) {
            map[row.provider_name] ??= row;
        }
        return map;
    }, [rows]);

    const provider = providers.find(item => item.id === providerId) ?? providers[0];
    const row = rowsByProvider[provider.id];
    const otherActive = rows.find(item => item.is_active && item.provider_name !== provider.id);
    const otherActiveName = otherActive
        ? (providers.find(item => item.id === otherActive.provider_name)?.name ??
          otherActive.provider_name)
        : undefined;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent closeLabel={t('integrations.actions.close')}>
                <DrawerHeading channel={channel} />
                <ServiceEditor
                    key={`${channel.id}:${provider.id}`}
                    channel={channel}
                    provider={provider}
                    providers={providers}
                    row={row}
                    otherActiveName={otherActiveName}
                    rowsByProvider={rowsByProvider}
                    onProviderChange={setProviderId}
                    onClose={() => onOpenChange(false)}
                    canEdit={canEdit}
                    canCheck={canCheck}
                />
            </SheetContent>
        </Sheet>
    );
};

export default ServiceDrawer;
